import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PayslipDialog — Admin: generate, preview and email a payslip.
 *
 * Uses the Gross+manual-deductions model:
 *   gross = hours × rate + Σ additions
 *   net   = gross − Σ deductions
 *
 * Admin enters deductions as free-form line items (PAYE Tax, NI,
 * Pension, Student Loan, etc.). No PAYE auto-calculation is performed
 * by design — payroll providers should still be the source of truth.
 *
 * Saves into the `payslips` collection on submit; optionally emails
 * the branded PDF straight to the staff member via the per-org SMTP
 * identity. After save, the dialog shows a list of past payslips with
 * download / re-send / mark-paid actions.
 */
function todayISO(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

function monthStart(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function monthEnd(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

export function PayslipDialog({ open, onClose, staffId, staffName, defaultRate, employmentType = 'employee', prefillHours, prefillPeriodStart, prefillPeriodEnd }) {
  const [periodStart, setPeriodStart] = useState(prefillPeriodStart || monthStart());
  const [periodEnd, setPeriodEnd] = useState(prefillPeriodEnd || monthEnd());
  const [payDate, setPayDate] = useState(todayISO());
  // Prefill hours from the PayrollCenter row when we opened the dialog
  // from there — saves the admin an extra "🔄 Pull" click.
  const [hours, setHours] = useState(
    prefillHours && prefillHours > 0 ? String(prefillHours) : ''
  );
  const [rate, setRate] = useState(defaultRate ?? '');
  const [additions, setAdditions] = useState([]);
  const [deductions, setDeductions] = useState([
    { label: 'Income Tax', amount: '' },
    { label: 'National Insurance', amount: '' },
  ]);
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Tracks the most recent timesheet pull so we can show a small
  // "✓ 24.5h from timesheet, 1 day flagged" hint next to the hours
  // field — and reset it when the admin starts typing manually.
  const [timesheetPull, setTimesheetPull] = useState(null);
  const [pulling, setPulling] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Phase N — Auto-calc state. Populated after a successful call to
  // /api/payroll/payslips/preview-auto so we can render the employer
  // cost box AND stamp the payslip with tax_tables_version on save.
  const [autoResult, setAutoResult] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);

  // Phase N.7 — Contractor / volunteer gating.
  // For contractors: hide Auto-calc + P60/P45 (statutory-employee only)
  // and expose a "Download remittance advice" button instead.
  // For volunteers: hide both (no payroll flow — they're unpaid).
  const empType = (employmentType || 'employee').toLowerCase();
  const isContractor = empType === 'contractor';
  const isVolunteer  = empType === 'volunteer';
  const isEmployee   = !isContractor && !isVolunteer;

  // Task 1 — Remittance history list for contractors (loaded lazily).
  const [remittanceHistory, setRemittanceHistory] = useState([]);
  const [remittanceHistoryLoading, setRemittanceHistoryLoading] = useState(false);
  const loadRemittanceHistory = async () => {
    if (!staffId || !isContractor) return;
    setRemittanceHistoryLoading(true);
    try {
      const rows = await api.listContractorRemittances(staffId);
      setRemittanceHistory(rows);
    } catch (err) {
      // Non-fatal — history is best-effort UI, keep the download flow usable.
      console.warn('Failed to load remittance history:', err);
    } finally {
      setRemittanceHistoryLoading(false);
    }
  };
  useEffect(() => {
    if (open && isContractor) loadRemittanceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staffId, isContractor]);

  useEffect(() => {
    if (!open) return;
    setRate(defaultRate ?? '');
    // Task 3 — auto-fill hours from PayrollCenter's aggregated total
    // if the parent passed one. Also refresh period start/end if the
    // parent narrowed them down (e.g. after a "Run for this period"
    // click). We deliberately don't overwrite manually-typed values
    // if the dialog re-mounts within the same session — the guard on
    // (open, staffId) means this fires only on a fresh open.
    if (prefillHours && prefillHours > 0) {
      setHours(String(prefillHours));
      setTimesheetPull({
        hours: prefillHours,
        flags: 0,
        source: 'payroll-center',
      });
    }
    if (prefillPeriodStart) setPeriodStart(prefillPeriodStart);
    if (prefillPeriodEnd)   setPeriodEnd(prefillPeriodEnd);
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staffId]);

  const loadHistory = async () => {
    if (!staffId) return;
    setHistoryLoading(true);
    try {
      const rows = await api.listStaffPayslips(staffId);
      setHistory(rows || []);
    } catch (err) {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * Pull the aggregated `worked_minutes` from the clock-events
   * timesheet for the currently-selected period and pre-fill the
   * Hours field. Also surfaces flagged days (missing clock-out,
   * unscheduled, etc.) as a small hint so the admin can audit before
   * sending the payslip.
   */
  const pullFromTimesheet = async () => {
    if (!staffId || !periodStart || !periodEnd) {
      toast.error('Set a period first');
      return;
    }
    if (periodEnd < periodStart) {
      toast.error('Period end must be on or after period start');
      return;
    }
    setPulling(true);
    try {
      const data = await api.getStaffTimesheet(staffId, periodStart, periodEnd);
      const entry = (data?.entries || [])[0];
      if (!entry) {
        toast.warning('No timesheet entries for this period');
        setTimesheetPull({ hours: 0, flaggedDays: 0, scheduledHours: 0, lateDays: 0, overtimeDays: 0 });
        setHours('0');
        return;
      }
      const workedMin = Number(entry?.totals?.worked_minutes || 0);
      const workedHours = Math.round((workedMin / 60) * 100) / 100; // 2dp
      const flaggedDays = (entry?.days || []).filter((d) =>
        Array.isArray(d.flags) && d.flags.length > 0,
      ).length;
      const scheduledHours = Math.round(
        ((entry?.totals?.scheduled_minutes || 0) / 60) * 100,
      ) / 100;
      setHours(String(workedHours));
      // If the profile rate hasn't been overridden, also pull it in so
      // gross/net update immediately.
      if (!rate && entry?.hourly_rate) {
        setRate(String(entry.hourly_rate));
      }
      setTimesheetPull({
        hours: workedHours,
        flaggedDays,
        scheduledHours,
        lateDays: entry?.totals?.late_days || 0,
        overtimeDays: entry?.totals?.overtime_days || 0,
      });
      toast.success(`Pulled ${workedHours}h from timesheet`);
    } catch (err) {
      toast.error(err.message || 'Could not pull timesheet');
    } finally {
      setPulling(false);
    }
  };

  /**
   * Quick period chips so admins can snap to common pay periods with a
   * single tap. The pay date defaults to the end of the period; admins
   * can still override it.
   */
  const setPeriodChip = (chip) => {
    const today = new Date();
    let s; let e;
    if (chip === 'this_month') {
      s = new Date(today.getFullYear(), today.getMonth(), 1);
      e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (chip === 'last_month') {
      s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      e = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (chip === 'this_week') {
      const day = today.getDay();
      s = new Date(today); s.setDate(today.getDate() - ((day + 6) % 7));
      e = new Date(s); e.setDate(s.getDate() + 6);
    } else if (chip === 'last_week') {
      const day = today.getDay();
      const monday = new Date(today); monday.setDate(today.getDate() - ((day + 6) % 7));
      s = new Date(monday); s.setDate(monday.getDate() - 7);
      e = new Date(s); e.setDate(s.getDate() + 6);
    } else if (chip === 'last_fortnight') {
      e = new Date(today);
      s = new Date(today); s.setDate(today.getDate() - 13);
    } else {
      return;
    }
    setPeriodStart(s.toISOString().slice(0, 10));
    setPeriodEnd(e.toISOString().slice(0, 10));
    setPayDate(e.toISOString().slice(0, 10));
    setTimesheetPull(null); // hours stale until next pull
  };

  const totals = useMemo(() => {
    const h = parseFloat(hours) || 0;
    const r = parseFloat(rate) || 0;
    const addSum = additions.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const dedSum = deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    const baseGross = h * r;
    const gross = baseGross + addSum;
    return {
      baseGross: baseGross.toFixed(2),
      addSum: addSum.toFixed(2),
      gross: gross.toFixed(2),
      dedSum: dedSum.toFixed(2),
      net: (gross - dedSum).toFixed(2),
    };
  }, [hours, rate, additions, deductions]);

  if (!open) return null;

  // Phase N — auto-calculate PAYE/NI/Pension/Student loan.
  //
  // Uses the staff's profile settings (tax_code, student_loan_plan,
  // pay_frequency, pension_opt_in, pension_contribution_pct) plus the
  // gross for this period (hours × rate + additions) to compute the
  // four statutory deduction lines. The admin can still edit any
  // amount afterwards — auto-calc REPLACES the deductions list, it
  // doesn't lock it.
  const handleAutoCalc = async () => {
    const h = parseFloat(hours) || 0;
    const r = parseFloat(rate) || 0;
    const addSum = additions.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const gross = +(h * r + addSum).toFixed(2);
    if (gross <= 0) {
      toast.error('Enter hours + rate (or additions) first — gross must be > 0');
      return;
    }
    setAutoLoading(true);
    try {
      const preview = await api.previewAutoPayslip({
        staff_id: staffId,
        period_start: periodStart,
        period_end: periodEnd,
        pay_date: payDate,
        period_gross: gross,
      });
      // Replace deductions with computed values (keep as strings so
      // the LineItemEditor's controlled inputs stay happy).
      setDeductions((preview.deductions || []).map((d) => ({
        label: d.label,
        amount: String(d.amount),
        note: d.note,
      })));
      setAutoResult(preview);
      const parts = preview.deductions.map((d) => `${d.label}: £${d.amount}`).join(' · ');
      toast.success(`Auto-calculated — ${parts || 'no deductions due'}`);
    } catch (err) {
      toast.error('Auto-calc failed: ' + err.message);
    } finally {
      setAutoLoading(false);
    }
  };

  // Bumping Auto-calc every time an input changes would be annoying
  // (and slow — it's an API call). We invalidate the stored result so
  // the "true cost" panel visually greys / clears until the admin
  // re-runs.
  const _invalidateAuto = () => setAutoResult(null);

  const submit = async () => {
    if (!hours || !rate) {
      toast.error('Hours and rate are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        period_start: periodStart,
        period_end: periodEnd,
        pay_date: payDate,
        hours_worked: parseFloat(hours),
        hourly_rate: parseFloat(rate),
        additions: additions
          .filter((a) => a.label && parseFloat(a.amount) > 0)
          .map((a) => ({ label: a.label, amount: parseFloat(a.amount), note: a.note || null })),
        deductions: deductions
          .filter((d) => d.label && parseFloat(d.amount) > 0)
          .map((d) => ({ label: d.label, amount: parseFloat(d.amount), note: d.note || null })),
        notes: notes || null,
        send_email: sendEmail,
      };
      const result = await api.createStaffPayslip(staffId, payload);
      if (result.email && result.email.ok === false) {
        toast.warning('Payslip saved but email failed: ' + (result.email.error || 'unknown'));
      } else if (sendEmail) {
        toast.success('Payslip created and emailed ✅');
      } else {
        toast.success('Payslip saved as draft');
      }
      // Reset form + reload history
      setHours(''); setNotes(''); setAdditions([]);
      setDeductions([{ label: 'Income Tax', amount: '' }, { label: 'National Insurance', amount: '' }]);
      loadHistory();
    } catch (err) {
      toast.error(err.message || 'Failed to create payslip');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const url = await api.downloadPayslipPdfUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err.message || 'Download failed');
    }
  };

  const handleResend = async (id) => {
    try {
      const r = await api.sendPayslipEmail(id);
      if (r?.ok) toast.success('Payslip re-sent ✅');
      else toast.error(r?.error || 'Send failed');
      loadHistory();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Mark this payslip as paid? This is book-keeping only — no payment is sent.')) return;
    try {
      await api.markPayslipPaid(id);
      toast.success('Marked as paid');
      loadHistory();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this DRAFT payslip permanently?')) return;
    try {
      await api.deletePayslip(id);
      toast.success('Deleted');
      loadHistory();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="payslip-dialog-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 300, display: 'flex', justifyContent: 'center',
        alignItems: 'flex-start', padding: '24px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="payslip-dialog"
        style={{
          background: '#fff', borderRadius: '12px', padding: '20px',
          width: '100%', maxWidth: '880px',
          boxShadow: '0 16px 40px rgba(15,23,42,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>💷 Generate payslip</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
              {staffName ? `For ${staffName}` : ''} — Gross + manual deductions model
            </p>
          </div>
          <button type="button" onClick={onClose} style={ghostBtn} data-testid="payslip-dialog-close">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.5rem 0.75rem' }}>
          <Field label="Period start">
            <input type="date" value={periodStart} onChange={(e) => { setPeriodStart(e.target.value); setTimesheetPull(null); }} data-testid="payslip-period-start" style={inputStyle} />
          </Field>
          <Field label="Period end">
            <input type="date" value={periodEnd} onChange={(e) => { setPeriodEnd(e.target.value); setTimesheetPull(null); }} data-testid="payslip-period-end" style={inputStyle} />
          </Field>
          <Field label="Pay date">
            <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payslip-pay-date" style={inputStyle} />
          </Field>
          <Field label="Hours worked">
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'stretch' }}>
              <input
                type="number" step="0.25" value={hours}
                onChange={(e) => { setHours(e.target.value); setTimesheetPull(null); }}
                data-testid="payslip-hours"
                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                onClick={pullFromTimesheet}
                disabled={pulling}
                data-testid="payslip-pull-from-timesheet"
                title="Pull aggregated worked hours from the clock-events timesheet for the selected period"
                style={{
                  padding: '0 0.6rem',
                  background: pulling ? '#9ca3af' : '#6366f1',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: pulling ? 'wait' : 'pointer',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {pulling ? '…' : '⤵ Pull'}
              </button>
            </div>
          </Field>
          <Field label="Hourly rate (£)">
            <input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="payslip-rate" style={inputStyle} />
          </Field>
        </div>

        {/* Quick period chips — snap to common pay periods then "⤵ Pull" */}
        <div style={{
          display: 'flex', gap: '0.35rem', flexWrap: 'wrap',
          alignItems: 'center', marginTop: '0.5rem', fontSize: '0.78rem',
        }}>
          <span style={{ color: '#6b7280', marginRight: '0.25rem' }}>Quick period:</span>
          {[
            { v: 'this_week',     l: 'This week' },
            { v: 'last_week',     l: 'Last week' },
            { v: 'last_fortnight',l: 'Last 14 days' },
            { v: 'this_month',    l: 'This month' },
            { v: 'last_month',    l: 'Last month' },
          ].map((c) => (
            <button
              key={c.v} type="button"
              onClick={() => setPeriodChip(c.v)}
              data-testid={`payslip-period-chip-${c.v}`}
              style={{
                padding: '0.25rem 0.6rem', borderRadius: '999px',
                border: '1px solid #d1d5db', background: '#fff',
                color: '#374151', cursor: 'pointer', fontWeight: 600,
              }}
            >
              {c.l}
            </button>
          ))}
        </div>

        {/* Timesheet pull hint — shown only after a successful pull. */}
        {timesheetPull && (
          <div
            data-testid="payslip-timesheet-pull-hint"
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '6px',
              fontSize: '0.8rem',
              color: '#3730a3',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>{timesheetPull.hours}h</strong> pulled from timesheet
              {timesheetPull.scheduledHours > 0 && (
                <> · {timesheetPull.scheduledHours}h scheduled</>
              )}
            </span>
            {timesheetPull.flaggedDays > 0 && (
              <span style={{ color: '#92400e', background: '#fef3c7', padding: '1px 8px', borderRadius: '999px', fontWeight: 700 }}>
                ⚠ {timesheetPull.flaggedDays} day(s) flagged
              </span>
            )}
            {timesheetPull.lateDays > 0 && (
              <span>⏰ {timesheetPull.lateDays} late</span>
            )}
            {timesheetPull.overtimeDays > 0 && (
              <span>+ {timesheetPull.overtimeDays} overtime</span>
            )}
            <span style={{ marginLeft: 'auto', color: '#6366f1', fontSize: '0.72rem' }}>
              Verify before sending.
            </span>
          </div>
        )}

        <LineItemEditor
          title="Additions (bonuses, expenses, reimbursements)"
          items={additions}
          setItems={setAdditions}
          testid="payslip-additions"
        />

        {/* Phase N — one-click "Auto-calculate" that fills in PAYE, NI,
            pension, and student-loan deductions using the staff's
            HMRC profile + this period's gross. Admin can then still
            edit any line by hand.
            Phase N.7 — hidden for non-employees: contractors settle
            their own tax via Self Assessment; volunteers are unpaid. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          margin: '0.75rem 0 0.25rem',
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
              Deductions (tax, NI, pension, student loan…)
            </label>
          </div>
          {isEmployee ? (
            <button
              type="button"
              onClick={handleAutoCalc}
              disabled={autoLoading}
              data-testid="payslip-auto-calc"
              title="Auto-calculate PAYE / NI / Pension / Student Loan from the staff profile"
              style={{
                padding: '0.35rem 0.8rem',
                background: autoLoading ? '#e5e7eb' : '#10b981',
                color: autoLoading ? '#6b7280' : '#fff',
                border: 'none', borderRadius: '6px', fontSize: '0.8rem',
                fontWeight: 700, cursor: autoLoading ? 'wait' : 'pointer',
              }}
            >
              {autoLoading ? 'Calculating…' : '⚡ Auto-calculate'}
            </button>
          ) : (
            <span
              data-testid="payslip-auto-calc-disabled-note"
              style={{
                fontSize: '0.72rem', color: '#6b7280', fontStyle: 'italic',
                padding: '0.25rem 0.55rem',
                background: '#fef3c7', border: '1px solid #fde68a',
                borderRadius: 6,
              }}
              title={
                isContractor
                  ? 'Contractors invoice their own fees — tax/NI settled via Self Assessment'
                  : 'Volunteers are unpaid — no payroll deductions apply'
              }
            >
              {isContractor
                ? '🧾 Contractor — use Remittance Advice below'
                : '🤝 Volunteer — no payroll deductions'}
            </span>
          )}
        </div>

        <LineItemEditor
          items={deductions}
          setItems={(v) => { setDeductions(v); _invalidateAuto(); }}
          testid="payslip-deductions"
        />

        <Field label="Notes (visible on payslip)" style={{ marginTop: '0.75rem' }}>
          <textarea
            rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="payslip-notes"
            style={{ ...inputStyle, width: '100%', fontFamily: 'inherit' }}
          />
        </Field>

        {/* Totals preview */}
        <div style={{
          marginTop: '1rem', padding: '0.75rem 1rem',
          background: '#f9fafb', borderRadius: '8px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: '0.5rem',
          fontSize: '0.85rem',
        }}>
          <Tile label="Base gross" value={`£${totals.baseGross}`} />
          <Tile label="+ Additions" value={`£${totals.addSum}`} color="#10b981" />
          <Tile label="Gross" value={`£${totals.gross}`} bold />
          <Tile label="− Deductions" value={`£${totals.dedSum}`} color="#ef4444" />
          <Tile label="NET PAY" value={`£${totals.net}`} highlight />
        </div>

        {/* Phase N — Employer costs + YTD panel (only after auto-calc). */}
        {autoResult && (
          <div
            data-testid="payslip-employer-cost-panel"
            style={{
              marginTop: '0.75rem', padding: '0.75rem 1rem',
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: '8px', fontSize: '0.82rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.4rem' }}>
              <strong style={{ color: '#0c4a6e' }}>Employer cost (this period)</strong>
              <span style={{ fontSize: '0.72rem', color: '#0369a1' }}>
                · tax tables {autoResult.tax_tables_version}
                {autoResult.period_number ? ` · period ${autoResult.period_number}` : ''}
              </span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '0.4rem',
            }}>
              <Tile label="Gross pay" value={`£${autoResult.employer_costs.true_employer_cost - autoResult.employer_costs.employer_ni - autoResult.employer_costs.pension_employer}`} />
              <Tile
                label="+ Employer NI"
                value={`£${autoResult.employer_costs.employer_ni.toFixed(2)}`}
                color="#f97316"
              />
              <Tile
                label="+ Employer pension"
                value={`£${autoResult.employer_costs.pension_employer.toFixed(2)}`}
                color="#8b5cf6"
              />
              <Tile
                label="TRUE COST"
                value={`£${autoResult.employer_costs.true_employer_cost.toFixed(2)}`}
                highlight
              />
            </div>
            {/* YTD after this period */}
            <div style={{ marginTop: '0.65rem', paddingTop: '0.5rem', borderTop: '1px dashed #7dd3fc' }}>
              <div style={{ marginBottom: '0.3rem', fontWeight: 600, color: '#0c4a6e', fontSize: '0.78rem' }}>
                YTD after this payslip · tax year {autoResult.tax_year}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.3rem',
                fontSize: '0.76rem',
              }}>
                <div>Gross: <strong>£{autoResult.ytd_including_this.gross.toFixed(2)}</strong></div>
                <div>Tax: <strong>£{autoResult.ytd_including_this.tax.toFixed(2)}</strong></div>
                <div>NI (emp): <strong>£{autoResult.ytd_including_this.employee_ni.toFixed(2)}</strong></div>
                <div>Pension (emp): <strong>£{autoResult.ytd_including_this.pension_employee.toFixed(2)}</strong></div>
                <div>Student loan: <strong>£{autoResult.ytd_including_this.student_loan.toFixed(2)}</strong></div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="payslip-send-email" />
            <span style={{ fontSize: '0.9rem' }}>📧 Email PDF to staff member immediately</span>
          </label>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button
            type="button" onClick={submit} disabled={submitting}
            data-testid="payslip-submit"
            style={{
              padding: '0.6rem 1.2rem', background: submitting ? '#9ca3af' : '#00BCD4',
              color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Saving…' : sendEmail ? '💾 Save & email' : '💾 Save draft'}
          </button>
        </div>

        {/* History */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '1rem', margin: 0 }}>Recent payslips</h3>
            <div style={{ flex: 1 }} />
            {/* Phase N — quick P60 / P45 downloads for the tax year.
                Phase N.7 — hidden for non-employees. */}
            {isEmployee && (
              <>
                <button
                  type="button"
                  data-testid="payslip-download-p60"
                  onClick={async () => {
                    const y = window.prompt('Tax year (e.g. 2025-26):', '2025-26');
                    if (!y) return;
                    try { await api.downloadP60(staffId, y.trim()); }
                    catch (err) { toast.error(err.message); }
                  }}
                  style={{
                    padding: '0.35rem 0.7rem', background: '#eef2ff',
                    border: '1px solid #c7d2fe', borderRadius: 6,
                    fontSize: '0.78rem', fontWeight: 600, color: '#3730a3',
                    cursor: 'pointer',
                  }}
                >
                  📄 Download P60
                </button>
                <button
                  type="button"
                  data-testid="payslip-download-p45"
                  onClick={async () => {
                    const d = window.prompt('Leaver date (YYYY-MM-DD) — leave blank for today:', '');
                    try { await api.downloadP45(staffId, d ? d.trim() : null); }
                    catch (err) { toast.error(err.message); }
                  }}
                  style={{
                    padding: '0.35rem 0.7rem', background: '#fff7ed',
                    border: '1px solid #fed7aa', borderRadius: 6,
                    fontSize: '0.78rem', fontWeight: 600, color: '#9a3412',
                    cursor: 'pointer',
                  }}
                >
                  👋 Download P45
                </button>
              </>
            )}
            {/* Phase N.7 — Contractor remittance advice.
                Builds a single line item from the hours × rate the
                admin already typed above (or additions if hours=0),
                and lets them add optional VAT + reference. */}
            {isContractor && (
              <button
                type="button"
                data-testid="payslip-download-remittance"
                onClick={async () => {
                  const h = parseFloat(hours) || 0;
                  const r = parseFloat(rate) || 0;
                  const baseAmount = +(h * r).toFixed(2);
                  if (baseAmount <= 0 && additions.length === 0) {
                    toast.error('Enter hours + rate (or additions) first');
                    return;
                  }
                  const reference = window.prompt('Invoice / remittance reference (optional):', '') || undefined;
                  const vatStr = window.prompt(
                    'VAT rate? Enter 0.20 for 20%, or leave blank for none:', '0'
                  );
                  let vatRate = 0;
                  if (vatStr && vatStr.trim()) {
                    const v = parseFloat(vatStr.trim());
                    if (!isNaN(v) && v >= 0 && v <= 1) vatRate = v;
                  }
                  const lineItems = [];
                  if (baseAmount > 0) {
                    lineItems.push({
                      label: `Services — ${periodStart} to ${periodEnd}`,
                      quantity: h || undefined,
                      unit_rate: r || undefined,
                      amount: baseAmount,
                    });
                  }
                  additions.forEach((a) => {
                    const amt = parseFloat(a.amount) || 0;
                    if (amt > 0) {
                      lineItems.push({
                        label: a.label || 'Additional charge',
                        amount: amt,
                        note: a.note,
                      });
                    }
                  });
                  try {
                    await api.downloadContractorRemittance(staffId, {
                      period_start: periodStart,
                      period_end: periodEnd,
                      payment_date: payDate,
                      line_items: lineItems,
                      vat_rate: vatRate,
                      reference,
                    });
                    toast.success('Remittance advice downloaded');
                    // Refresh so the newly-saved row appears in history
                    loadRemittanceHistory();
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                style={{
                  padding: '0.35rem 0.7rem', background: '#fef3c7',
                  border: '1px solid #fde68a', borderRadius: 6,
                  fontSize: '0.78rem', fontWeight: 600, color: '#78350f',
                  cursor: 'pointer',
                }}
              >
                🧾 Download Remittance Advice
              </button>
            )}
          </div>
          {historyLoading ? (
            <div style={{ color: '#6b7280' }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No payslips yet for this staff member.</div>
          ) : (
            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                  <th style={th}>Period</th>
                  <th style={th}>Gross</th>
                  <th style={th}>Net</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={td}>{p.period_start} → {p.period_end}</td>
                    <td style={td}>£{(p.gross ?? 0).toFixed(2)}</td>
                    <td style={td}><strong>£{(p.net ?? 0).toFixed(2)}</strong></td>
                    <td style={td}>
                      <span style={pillStyle(p.status)}>{p.status}</span>
                    </td>
                    <td style={td}>
                      <button onClick={() => handleDownload(p._id)} style={miniBtn} data-testid={`payslip-download-${p._id}`}>⬇ PDF</button>
                      <button onClick={() => handleResend(p._id)} style={miniBtn} data-testid={`payslip-send-${p._id}`}>📧 Send</button>
                      {p.status !== 'paid' && (
                        <button onClick={() => handleMarkPaid(p._id)} style={miniBtn}>✓ Paid</button>
                      )}
                      {p.status === 'draft' && (
                        <button onClick={() => handleDelete(p._id)} style={{ ...miniBtn, color: '#ef4444' }}>🗑</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Task 1 — Remittance history (contractors only). Shows
              previously-generated remittances so admins can
              re-download without regenerating (which could produce a
              slightly different PDF if org branding / VAT / rates
              have changed since). */}
          {isContractor && (
            <div style={{ marginTop: '1.5rem' }} data-testid="remittance-history-panel">
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Previous remittance advices</h3>
              {remittanceHistoryLoading ? (
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading…</div>
              ) : remittanceHistory.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No remittance advices yet — generate one above.</div>
              ) : (
                <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', color: '#374151' }}>
                      <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Payment date</th>
                      <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Period</th>
                      <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Reference</th>
                      <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Total</th>
                      <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {remittanceHistory.map((r) => (
                      <tr
                        key={r._id}
                        data-testid={`remittance-history-row-${r._id}`}
                        style={{ borderTop: '1px solid #e5e7eb' }}
                      >
                        <td style={{ padding: '0.35rem 0.5rem' }}>{r.payment_date || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: '#6b7280' }}>
                          {r.period_start} → {r.period_end}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{r.reference || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>
                          £{Number(r.total_payable || 0).toFixed(2)}
                          {r.vat_rate ? (
                            <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.72rem' }}>
                              {' '}(inc. VAT {(r.vat_rate * 100).toFixed(0)}%)
                            </span>
                          ) : null}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                          {r.has_pdf ? (
                            <button
                              type="button"
                              data-testid={`remittance-redownload-${r._id}`}
                              onClick={async () => {
                                try { await api.downloadSavedRemittance(r._id); }
                                catch (err) { toast.error(err.message); }
                              }}
                              style={{ ...miniBtn, color: '#3730a3' }}
                              title="Re-download the exact PDF generated at the time"
                            >
                              ⬇
                            </button>
                          ) : (
                            <span
                              style={{ color: '#9ca3af', fontSize: '0.72rem' }}
                              title="This remittance predates PDF persistence — no stored copy."
                            >
                              (no copy)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LineItemEditor({ title, items, setItems, testid }) {
  const update = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, { label: '', amount: '' }]);
  return (
    <div style={{ marginTop: '1rem' }} data-testid={testid}>
      {title && <h4 style={{ margin: '0 0 0.4rem 0', fontSize: '0.9rem' }}>{title}</h4>}
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.35rem' }}>
          <input
            type="text" placeholder="Label" value={it.label}
            onChange={(e) => update(i, { label: e.target.value })}
            style={{ ...inputStyle, flex: 2 }}
          />
          <input
            type="number" step="0.01" placeholder="0.00" value={it.amount}
            onChange={(e) => update(i, { amount: e.target.value })}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button type="button" onClick={() => remove(i)} style={ghostBtn}>✕</button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ ...ghostBtn, fontSize: '0.85rem' }}>+ Add line</button>
    </div>
  );
}

function Field({ label, children, style }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.78rem', color: '#6b7280', ...style }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Tile({ label, value, bold, highlight, color }) {
  return (
    <div style={{
      background: highlight ? '#00BCD4' : '#fff',
      color: highlight ? '#fff' : (color || '#1f2937'),
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '0.5rem 0.75rem',
    }}>
      <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{label}</div>
      <div style={{ fontWeight: bold || highlight ? 700 : 600, fontSize: '1rem' }}>{value}</div>
    </div>
  );
}

const inputStyle = { padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' };
const ghostBtn = { padding: '0.4rem 0.7rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 };
const miniBtn = { padding: '0.25rem 0.5rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '0.75rem', marginRight: '0.2rem' };
const th = { padding: '0.45rem', fontWeight: 700, fontSize: '0.78rem' };
const td = { padding: '0.45rem' };
const pillStyle = (status) => ({
  padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
  background: status === 'paid' ? '#dcfce7' : status === 'sent' ? '#dbeafe' : '#fef3c7',
  color:      status === 'paid' ? '#166534' : status === 'sent' ? '#1e40af' : '#92400e',
});

export default PayslipDialog;
